import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Copy,
    Check,
    Trash2,
    ExternalLink,
    Clock,
    Globe
} from "lucide-react";

const Shorten = ({ icon, short_URI, long_URI, expireAt, onDelete }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
        if (short_URI) {
            navigator.clipboard.writeText(short_URI);
            setCopied(true);
        }
    };

    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => {
                setCopied(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    const formatUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname + (urlObj.pathname !== '/' ? urlObj.pathname : '');
        } catch {
            return url;
        }
    };

    const isExpiringSoon = () => {
        if (!expireAt) return false;
        const expiryDate = new Date(expireAt);
        const now = new Date();
        const hoursUntilExpiry = (expiryDate - now) / (1000 * 60 * 60);
        return hoursUntilExpiry <= 24 && hoursUntilExpiry > 0;
    };

    const isExpired = () => {
        if (!expireAt) return false;
        return new Date(expireAt) < new Date();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
        >
            {/* Status indicator */}
            <div className="absolute top-3 right-3">
                {isExpired() ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                        Expired
                    </span>
                ) : isExpiringSoon() ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                        Expiring Soon
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                        Active
                    </span>
                )}
            </div>

            <div className="p-5">
                {/* Header with icon and main URL */}
                <div className="flex items-start gap-4 mb-4">
                    {/* Enhanced icon container */}
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 border border-gray-200 shadow-sm">
                            <img
                                src={icon}
                                alt="Website icon"
                                className="w-full h-full object-contain rounded-lg"
                                onError={(e) => {
                                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%236366f1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'/%3E%3Cpath d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'/%3E%3C/svg%3E";
                                }}
                            />
                        </div>
                    </div>

                    {/* URL information */}
                    <div className="flex-1 min-w-0">
                        {/* Short URL */}
                        {short_URI && (
                            <div className="mb-2">
                                <a
                                    href={short_URI}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 group/link"
                                >
                                    <span className="truncate">{short_URI}</span>
                                    <ExternalLink className="w-4 h-4 text-indigo-400 group-hover/link:text-purple-500 flex-shrink-0" />
                                </a>
                            </div>
                        )}

                        {/* Original URL */}
                        {long_URI && (
                            <div className="mb-3">
                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <Globe className="w-4 h-4 flex-shrink-0" />
                                    <a
                                        href={long_URI}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="truncate hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200"
                                        title={long_URI}
                                    >
                                        {formatUrl(long_URI)}
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Expiry information */}
                        {expireAt && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                <Clock className="w-3 h-3" />
                                <span>
                                    Expires: {new Date(expireAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {/* Copy button */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200
              ${copied
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700 border border-indigo-200 hover:border-indigo-300'
                            }
            `}
                        title={copied ? "Copied!" : "Copy to clipboard"}
                        onClick={copyToClipboard}
                        disabled={copied}
                    >
                        {copied ? (
                            <>
                                <Check className="w-4 h-4" />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy className="w-4 h-4" />
                                Copy
                            </>
                        )}
                    </motion.button>

                    {/* Delete button */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium text-sm border border-red-200 hover:border-red-300 transition-all duration-200"
                        title="Delete this link"
                        onClick={onDelete}
                    >
                        <Trash2 className="w-4 h-4" />
                        Delete
                    </motion.button>

                    {/* Visit button */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm border border-gray-200 dark:border-gray-600 transition-all duration-200"
                        onClick={() => window.open(short_URI, '_blank')}
                        title="Visit link"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Visit
                    </motion.button>
                </div>
            </div>

            {/* Gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </motion.div>
    );
};

export default Shorten;
